import * as React from 'react';
import { Form, FormComponent } from './Form';
import { Dialog, showCustom, DialogActions, showMessage, DialogBody, DialogHeader, DialogFooter } from './Dialog';
import { whenEscKey, whenEnterKey, formatValue, Format, formatAsElementId, debounce } from './Utils';
import { Res } from '../../api';
import classnames from 'classnames';
import Button from './Button';
import ErrorBlock from './ErrorBlock';

import { Binding, Result } from 'core/infrastructure';
import { DataGrid } from './DataGrid';
import { DataGridColumn } from './DataGrid.Column';
import { Row, Column } from './Grid';
import { TextBox } from './TextBox';
import * as TagUtils from './TagUtils';
import { createTagsFilter } from './createTagsFilter';

interface OptionsGridProps<TOption> extends React.HTMLAttributes<OptionsGridProps<TOption>> {
	source: TOption[] | ((filter: string) => Promise<TOption[]>);
	sourceKey: (item: TOption) => string | number;
	columns: DataGridColumn<TOption>[];
	allowedTags?: string[];
}

interface OptionsGridState<TOption> {
	searchParams: {
		filterText: string,
		tags: string[]
	},
	options: TOption[];
}

type SearchParams = OptionsGridState<unknown>['searchParams'];

class OptionsGrid<TOption> extends React.Component<OptionsGridProps<TOption>, OptionsGridState<TOption>> {

	private mounted = false;
	private dataGrid = React.createRef<DataGrid<TOption>>();

	private form = React.createRef<Form<SearchParams>>();

	constructor(props: OptionsGridProps<TOption>) {
		super(props);

		this.state = {
			options: props.source instanceof Array
				? this.getValidOptions()
				: [],
			searchParams: {
				filterText: '',
				tags: []
			}
		};
	}

	private getValidOptions(): TOption[] {
		if (this.props.source instanceof Array)
			return TagUtils.filterByTags(this.props.source, this.props.allowedTags ?? []);

		throw Error('getValidOptions cannot be calculated if source is remote');
	}

	componentDidMount() {
		this.mounted = true;
	}

	componentWillUnmount() {
		this.mounted = false;
	}

	getSelectedItem(): Nullable<TOption> {
		return this.dataGrid.current!.getSelectedItems()[0] || null;
	}

	private ensureDataGridSelection() {
		if (this.dataGrid.current!.getSelectedItems().length === 0)
			this.dataGrid.current!.selectFirstItem();
	}

	private searchLocal = debounce(async () => {
		// Por el debounce podría dispararse esto cuando ya no está montado el componente
		if (this.mounted) {
			await this.form.current!.ifValid().then(({ filterText, tags }) => {
				this.setState({
					options: TagUtils.filterByTags(this.getValidOptions(), tags)
				}, () => {
					this.dataGrid.current!.filterBy(filterText);
					this.ensureDataGridSelection();
				});
			});
		}
	});

	private searchRemote = async () => {

		if (this.props.source instanceof Array)
			throw Error('Esta función no se debería llamar si el origen de datos es local');

		const searchFunc = this.props.source;

		const options = await this.form.current!.ifValid().then(searchParams => searchFunc(searchParams.filterText));
		// Podrían desmontar el componente mientras se ejecuta la búsqueda
		if (this.mounted)
			this.setState({ options }, () => this.ensureDataGridSelection());
	};

	private useLocalSearch(): boolean {
		return this.props.source instanceof Array;
	}

	private onInputKeyDown = async (e: React.KeyboardEvent<unknown>) => {
		if (this.dataGrid.current?.handleExternalKeyDown(e))
			return;

		// Si es búsqueda local, la lanzo (ya está debounceada)
		if (this.useLocalSearch()) {
			this.searchLocal();
			return;
		}

		// Estoy en una búsqueda contra el servidor. Si no tengo nada en el grid, lanzo
		// la búsqueda. Si tengo algo, dejo que el diálogo que sacó este componente se
		// cierre con el item seleccionado
		if (e.key === 'Enter' && this.dataGrid.current!.getSelectedItems().length === 0) {
			e.preventDefault();
			e.stopPropagation();
			await this.searchRemote();
		}
		else {
			this.setState({ options: [] });
		}
	};


	private buildLocalSearchControl() {
		return <Row>
			<Column width='full'>
				<TextBox<SearchParams>
					autoFocus
					autoComplete='off'
					linkTo={x => x.filterText}
					placeholder={Res.WA.WA_SEARCH_PLACE_HOLDER()}
					onKeyDown={this.onInputKeyDown}
				/>
				{createTagsFilter<SearchParams, TOption>({
					linkTo: x => x.tags,
					allowedTags: this.props.allowedTags,
					maybeTaggableItems: this.getValidOptions(),
					onValueChanged: this.searchLocal
				})}
			</Column>
		</Row>;
	}

	private buildRemoteSearchControl() {
		return <Row>
			<div className='col-xs-10'>
				<TextBox<SearchParams>
					linkTo={x => x.filterText}
					placeholder={Res.WA.WA_SEARCH_PLACE_HOLDER()}
					onKeyDown={this.onInputKeyDown}
				/>
			</div>
			<div className='col-xs-2'>
				<Button buttonStyle='info' className='pull-right' onClick={this.searchRemote}><i className='fa fa-search'></i> {Res.Common.SEARCH_BTN()}</Button>
			</div>
		</Row>;
	}

	render() {
		return <div>
			<Form<SearchParams> model={this.state.searchParams} ref={this.form}>
				{this.useLocalSearch() ? this.buildLocalSearchControl() : this.buildRemoteSearchControl()}
			</Form>
			<Row className='m-t' >
				<Column width='full'>
					<DataGrid<TOption>
						height={300}
						itemKey={this.props.sourceKey}
						ref={this.dataGrid}
						id={this.props.id || 'select_datagrid_item'}
						columns={this.props.columns}
						initialData={this.state.options}
						shouldPersistState={false}
						hideExportButton={true}
						multiSelect={false}
						maxHeight={400} />
				</Column>
			</Row>
		</div>;
	}
}

type OptionsGridCtor<TOptions> = new (props: OptionsGridProps<TOptions>) => OptionsGrid<TOptions>;

interface SelectDialogProps<TOption> extends React.HTMLAttributes<SelectDialog<TOption>> {
	grid: OptionsGridProps<TOption>;
	title: string;
	actions?: DialogActions<TOption>;
}

class SelectDialog<TOption> extends React.Component<SelectDialogProps<TOption>, EmptyState> {

	private optionsGrid = React.createRef<OptionsGrid<TOption>>();

	constructor(props: SelectDialogProps<TOption>) {
		super(props);
	}

	render() {

		const onAccept = async () => {

			if (!this.props.actions)
				return;

			const selectedOption = this.optionsGrid.current!.getSelectedItem();

			if (selectedOption === null) {
				await showMessage(Res.Msg.MSG_MUST_SELECT_A_VALUE());
				return;
			}

			this.props.actions.closeWith(selectedOption);
		};

		const onCancel = () => this.props.actions && this.props.actions.cancel();

		const onDefaultKeyDown = (e: React.KeyboardEvent<any>) => {
			whenEnterKey(onAccept)(e);
			whenEscKey(onCancel)(e);
		};

		const Grid: OptionsGridCtor<TOption> = OptionsGrid;

		return <Dialog onKeyDown={onDefaultKeyDown}>
			<DialogHeader>
				<h4 className='modal-title'>{this.props.title}</h4>
			</DialogHeader>
			<DialogBody>
				<Grid ref={this.optionsGrid} {... this.props.grid} />
			</DialogBody>
			<DialogFooter>
				<Button buttonStyle='info' onClick={onAccept}>{Res.Common.OK_BTN()}</Button>
				<Button onClick={onCancel}>{Res.Common.CANCEL_BTN()}</Button>
			</DialogFooter>
		</Dialog>;
	}
}

interface SelectInputProps<TModel, TOption, TBoundValue> extends React.HTMLProps<SelectInput<TModel, TOption, TBoundValue>> {

	label?: string;
	linkTo: Binding.PropertyPath<TModel>;

	formatBoundValue?: Format;
	emptySelectionText: string;

	source: TOption[] | ((filter: string) => Promise<TOption[]>);

	dialogTitle: string;
	dialogColumns: DataGridColumn<TOption>[];
	dialogOptionKey: (option: TOption) => string | number;

	convertSelectedOptionToBoundValue: (option: TOption) => TBoundValue;
	onOptionSelected?: (option: TOption) => void;

	boundValue?: TBoundValue;
	formId?: string;

	allowRemoveValue?: boolean

	allowedTags?: string[]
}

interface SelectInputState<TBoundValue> {
	errorMsg?: string;
	selectedValue: Nullable<TBoundValue>;
}

export class SelectInput<TModel, TOption, TBoundValue> extends React.Component<SelectInputProps<TModel, TOption, TBoundValue>, SelectInputState<TBoundValue>> implements FormComponent {

	constructor(props: SelectInputProps<TModel, TOption, TBoundValue>) {

		super(props);

		this.state = this.createInitialState(props);
	}

	createInitialState(props: SelectInputProps<TModel, TOption, TBoundValue>): SelectInputState<TBoundValue> {
		return {
			errorMsg: '',
			selectedValue: typeof props.boundValue === 'undefined' ? null : props.boundValue,
		};
	}

	getPath(): string {
		return Binding.propertyPathToString(this.props.linkTo);
	}

	showErrors(errors: string[]): void {
		this.setState({ errorMsg: errors.join(' ') });
	}

	getValue(): Result<Nullable<TBoundValue>> {
		this.setState({ errorMsg: '' });
		const value = this.state.selectedValue;

		if (this.props.required && (value === null || value === undefined)) {
			this.setState({ errorMsg: Res.Msg.MSG_VALUE_REQUIRED() });
			return Result.error();
		}

		return Result.ok(value);
	}

	private getValueDescription(): string {
		const selectedValue = this.state.selectedValue;
		return selectedValue !== null
			? formatValue(selectedValue, this.props.formatBoundValue)
			: this.props.emptySelectionText || '';
	}

	protected showOptionsDialog(): Promise<Result<TOption>> {
		return showCustom(<SelectDialog<TOption>
			title={this.props.dialogTitle}
			grid={{
				source: this.props.source,
				columns: this.props.dialogColumns,
				sourceKey: this.props.dialogOptionKey,
				allowedTags: this.props.allowedTags
			}} />);
	}

	render() {

		const divRef = React.createRef<HTMLDivElement>();

		const onShowOptions = async () => {
			divRef.current?.focus();

			await this.showOptionsDialog().then(result => {
				if (!result.ok)
					return;

				const value = this.props.convertSelectedOptionToBoundValue(result.value);
				this.setState({ selectedValue: value },
					() => this.props.onOptionSelected && this.props.onOptionSelected(result.value));
			});
		};

		const inputId = formatAsElementId(this.getPath(), this.props.formId || '');
		const selectionDescription = this.getValueDescription();

		const onDivKeyDown = async (e: React.KeyboardEvent<HTMLDivElement>) => {
			if (e.key === ' ' || e.key === 'F3') {
				e.preventDefault();
				e.stopPropagation();
				await onShowOptions();
			}
		};

		const onRemoveButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
			if (this.props.disabled)
				return;

			e.preventDefault();
			e.stopPropagation();
			this.setState({ selectedValue: null });
		};

		const onSearchButtonClick = async (e: React.MouseEvent<HTMLButtonElement>) => {
			if (this.props.disabled)
				return;

			e.preventDefault();
			e.stopPropagation();
			await onShowOptions();
		};

		return <div className={classnames({ 'form-group': true, 'has-error': !!this.state.errorMsg })}>
			{this.props.label ?
				<label className='col-sm-4 control-label' htmlFor={inputId}>
					{this.props.label}
				</label> : null}

			<div className={this.props.label ? 'col-sm-8' : 'col-sm-12'} onKeyDown={onDivKeyDown} >
				<div className='input-group' tabIndex={0} ref={divRef} style={{ borderRadius: 4 }} onClick={this.props.disabled || this.props.allowRemoveValue === true ? () => { } : onShowOptions}>
					<input tabIndex={-1} type='text' id={inputId} className='form-control' value={selectionDescription} readOnly style={{ background: 'white', cursor: 'pointer', color: '#555' }} disabled={!!this.props.disabled} />
					<span className='input-group-btn'>
						<Button tabIndex={-1} disabled={!!this.props.disabled} onClick={onSearchButtonClick}><i className='text-info fa fa-search'></i></Button>
						{
							this.props.allowRemoveValue
								? <Button tabIndex={-2} disabled={!!this.props.disabled} onClick={onRemoveButtonClick}><i className='text-info fa fa-times'></i></Button>
								: null
						}
					</span>
				</div>
				<ErrorBlock msg={this.state.errorMsg} />
			</div>
		</div>;
	}
}

export type SelectInputCtor<TModel, TOption, TBoundValue> = new (props: SelectInputProps<TModel, TOption, TBoundValue>) => SelectInput<TModel, TOption, TBoundValue>;
